import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Languages, MessageCircle, Phone, Plus, Minus, AlertCircle, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1614">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1615">
        <span className="text-lg font-medium text-gray-900 group-hover:text-forest-700 transition-colors" data-id="element-1616">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-100 text-gray-400'}`} data-id="element-1617">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1618" /> : <Plus className="w-4 h-4" data-id="element-1619" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1620">
        <p className="text-gray-600 leading-relaxed" data-id="element-1621">{answer}</p>
      </div>
    </div>;
}
export function EmergencyDentistLondonPage() {
  const {
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const pick = (en: string, ua: string, ru: string, ro: string) => {
    if (lang === 'ua') return ua;
    if (lang === 'ru') return ru;
    if (lang === 'ro') return ro;
    return en;
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const seoTitle = pick('Emergency Dentist in Angel, London | Dentico Clinic', 'Невідкладний стоматолог у Лондоні (Angel) | Dentico Clinic', 'Неотложный стоматолог в Лондоне (Angel) | Dentico Clinic', 'Stomatolog de Urgență în Angel, Londra | Dentico Clinic');
  const seoDescription = pick('Need an emergency dentist in Angel? Dentico Clinic offers urgent dental appointments for toothache, broken teeth, swelling and dental trauma.', 'Потрібен невідкладний стоматолог в Angel? Dentico Clinic пропонує термінові прийоми при зубному болю, зламаних зубах, набряках та травмах.', 'Нужен неотложный стоматолог в Angel? Dentico Clinic предлагает срочные приёмы при зубной боли, сломанных зубах, отёках и травмах.', 'Aveți nevoie de un stomatolog de urgență în Angel? Dentico Clinic oferă programări urgente pentru durere de dinți, dinți rupți, umflături și traume dentare.');
  const faqs = [{
    q: pick('What should I do if I have severe toothache?', 'Що робити, якщо сильно болить зуб?', 'Что делать, если сильно болит зуб?', 'Ce ar trebui să fac dacă am o durere de dinți severă?'),
    a: pick('Contact us as soon as possible for an urgent examination. We will identify the cause of the pain and offer treatment. Do not apply heat to the affected area!', "Якнайшвидше зв'яжіться з нами для невідкладного огляду. Ми визначимо причину болю та запропонуємо лікування. Не грійте хворе місце!", 'Свяжитесь с нами как можно скорее для неотложного осмотра. Мы определим причину боли и предложим лечение. Не грейте больное место!', 'Contactați-ne cât mai curând posibil pentru o examinare urgentă. Vom identifica cauza durerii și vom oferi tratament. Nu aplicați căldură pe zona afectată!')
  }, {
    q: pick('Do you offer same-day emergency appointments?', 'Чи приймаєте ви терміново в той же день?', 'Принимаете ли вы срочно в тот же день?', 'Oferiți programări de urgență în aceeași zi?'),
    a: pick('Yes, we always try to find time for patients with acute pain on the same day. Message us on WhatsApp or call us.', 'Так, ми завжди намагаємося знайти час для пацієнтів з гострим болем у той же день. Напишіть у WhatsApp або зателефонуйте.', 'Да, мы всегда стараемся найти время для пациентов с острой болью в тот же день. Напишите в WhatsApp или позвоните.', 'Da, încercăm întotdeauna să găsim timp pentru pacienții cu durere acută în aceeași zi. Scrieți-ne pe WhatsApp sau sunați-ne.')
  }, {
    q: pick('How much does an emergency appointment cost?', 'Скільки коштує екстрений прийом?', 'Сколько стоит экстренный приём?', 'Cât costă o programare de urgență?'),
    a: pick('The cost depends on the procedures and diagnostics required. An emergency consultation is £60. After the examination, we will explain the treatment options and prices.', 'Вартість залежить від необхідних процедур та діагностики. Екстрена консультація — £60. Після огляду ми пояснимо варіанти лікування та ціни.', 'Стоимость зависит от необходимых процедур и диагностики. Экстренная консультация — £60. После осмотра мы объясним варианты лечения и цены.', 'Costul depinde de procedurile și diagnosticul necesar. O consultație de urgență costă 60£. După examinare, vom explica opțiunile de tratament și prețurile.')
  }, {
    q: pick('Which symptoms are considered an emergency?', 'Які симптоми вважаються невідкладними?', 'Какие симптомы считаются неотложными?', 'Ce simptome sunt considerate o urgență?'),
    a: pick('Severe toothache, swelling of the face or gums, pus, fever, pain when swallowing, dental trauma (chip, dislocation), bleeding after an extraction.', 'Сильний зубний біль, набряк обличчя або ясен, гній, підвищена температура, біль при ковтанні, травма зуба (скол, вивих), кровотеча після видалення.', 'Сильная зубная боль, отёк лица или дёсен, гной, повышенная температура, боль при глотании, травма зуба (скол, вывих), кровотечение после удаления.', 'Durere dentară severă, umflarea feței sau a gingiilor, puroi, febră, durere la înghițire, traumă dentară (ciobire, luxație), sângerare după o extracție.')
  }, {
    q: pick('What should I do if a tooth gets knocked out?', 'Що робити, якщо вибили зуб?', 'Что делать, если выбили зуб?', 'Ce ar trebui să fac dacă un dinte este lovit și cade?'),
    a: pick('Find the tooth, hold it by the crown (not the root!), rinse with water if dirty. Try to place it back in the socket or store it in milk/saliva. See a dentist within 30-60 minutes!', 'Знайдіть зуб, тримайте його за коронку (не за корінь!), промийте водою, якщо брудний. Спробуйте вставити назад у лунку або помістіть у молоко/слину. Зверніться до лікаря протягом 30-60 хвилин!', 'Найдите зуб, держите его за коронку (не за корень!), промойте водой, если он грязный. Попробуйте вставить обратно в лунку или поместите в молоко/слюну. Обратитесь к врачу в течение 30-60 минут!', 'Găsiți dintele, țineți-l de coroană (nu de rădăcină!), clătiți cu apă dacă este murdar. Încercați să-l puneți înapoi în alveolă sau păstrați-l în lapte/salivă. Mergeți la dentist în 30-60 de minute!')
  }, {
    q: pick('Do you work on weekends?', 'Чи працюєте ви у вихідні?', 'Работаете ли вы по выходным?', 'Lucrați în weekend?'),
    a: pick('We work Monday to Saturday. In emergencies, message us on WhatsApp and we will try to help or advise you.', 'Ми працюємо з понеділка по суботу. У невідкладних випадках напишіть нам у WhatsApp, ми спробуємо допомогти або порадити.', 'Мы работаем с понедельника по субботу. В неотложных случаях напишите нам в WhatsApp, мы постараемся помочь или посоветовать.', 'Lucrăm de luni până sâmbătă. În caz de urgență, scrieți-ne pe WhatsApp și vom încerca să vă ajutăm sau să vă sfătuim.')
  }, {
    q: pick('Can I get help in my own language?', 'Чи можна отримати допомогу українською?', 'Можно ли получить помощь на русском?', 'Pot primi ajutor în limba mea?'),
    a: pick('Yes, our team at Dentico Clinic speaks English, Ukrainian, Russian and Romanian, so you can describe your symptoms precisely.', 'Так, у Dentico Clinic можливе спілкування українською мовою. Ви зможете точно описати свої симптоми.', 'Да, в Dentico Clinic возможно общение на русском языке. Вы сможете точно описать свои симптомы.', 'Da, echipa noastră de la Dentico Clinic vorbește engleză, ucraineană, rusă și română, astfel încât să vă puteți descrie cu precizie simptomele.')
  }, {
    q: pick('Where are you located?', 'Де ви знаходитесь?', 'Где вы находитесь?', 'Unde sunteți situați?'),
    a: '360–364 City Road, London EC1V 2PY (Angel / Islington).'
  }];
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Emergency Dental Care',
    description: 'Urgent dental treatment for pain relief, trauma, or infection.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  const whenCards = [{
    title: pick('Severe toothache', 'Сильний зубний біль', 'Сильная зубная боль', 'Durere dentară severă'),
    desc: pick('Pain that does not respond to painkillers, throbbing pain or pain at night.', 'Біль, який не проходить від знеболюючих, пульсуючий або нічний біль.', 'Боль, которая не проходит от обезболивающих, пульсирующая или ночная боль.', 'Durere care nu răspunde la analgezice, durere pulsatilă sau durere pe timp de noapte.')
  }, {
    title: pick('Facial swelling', 'Набряк обличчя', 'Отёк лица', 'Umflarea feței'),
    desc: pick('Swelling of the cheek or gums may indicate a serious infection.', 'Припухлість щоки або ясен може свідчити про серйозну інфекцію.', 'Припухлость щеки или дёсен может свидетельствовать о серьёзной инфекции.', 'Umflarea obrazului sau a gingiilor poate indica o infecție gravă.')
  }, {
    title: pick('Dental trauma', 'Травма зуба', 'Травма зуба', 'Traumă dentară'),
    desc: pick('A broken, chipped or knocked-out tooth caused by a blow or fall.', 'Зламаний, сколотий або вибитий зуб внаслідок удару чи падіння.', 'Сломанный, сколотый или выбитый зуб в результате удара или падения.', 'Un dinte rupt, ciobit sau lovit cauzat de o lovitură sau o cădere.')
  }, {
    title: pick('Lost filling/crown', 'Втрата пломби/коронки', 'Потеря пломбы/коронки', 'Pierderea unei plombe/coroane'),
    desc: pick('If a filling or crown falls out and the tooth becomes sensitive or sharp.', 'Якщо випала пломба або коронка, і зуб став чутливим або гострим.', 'Если выпала пломба или коронка, и зуб стал чувствительным или острым.', 'Dacă o plombă sau o coroană cade și dintele devine sensibil sau ascuțit.')
  }, {
    title: pick('Bleeding', 'Кровотеча', 'Кровотечение', 'Sângerare'),
    desc: pick('Bleeding from the gums after an extraction that will not stop.', 'Кровотеча з ясен після видалення зуба, яка не зупиняється.', 'Кровотечение из дёсен после удаления зуба, которое не останавливается.', 'Sângerare a gingiilor după o extracție care nu se oprește.')
  }, {
    title: pick('Pain when swallowing', 'Біль при ковтанні', 'Боль при глотании', 'Durere la înghițire'),
    desc: pick('Difficulty swallowing or breathing is a sign of a dangerous spreading infection.', 'Ускладнене ковтання або дихання — це ознака небезпечного поширення інфекції.', 'Затруднённое глотание или дыхание — это признак опасного распространения инфекции.', 'Dificultatea de a înghiți sau de a respira este un semn al unei infecții periculoase care se răspândește.')
  }];
  const processSteps = [{
    step: '01',
    title: pick('Contact', "Зв'язок", 'Связь', 'Contact'),
    desc: pick('Message us on WhatsApp or call us. Describe your symptoms.', 'Напишіть нам у WhatsApp або зателефонуйте. Опишіть симптоми.', 'Напишите нам в WhatsApp или позвоните. Опишите симптомы.', 'Scrieți-ne pe WhatsApp sau sunați-ne. Descrieți-vă simptomele.')
  }, {
    step: '02',
    title: pick('Assessment', 'Оцінка', 'Оценка', 'Evaluare'),
    desc: pick('We will assess the urgency and find the soonest available appointment.', 'Ми оцінимо терміновість і знайдемо найближчий час для візиту.', 'Мы оценим срочность и найдём ближайшее время для визита.', 'Vom evalua urgența și vom găsi cea mai apropiată programare disponibilă.')
  }, {
    step: '03',
    title: pick('Visit', 'Візит', 'Визит', 'Vizită'),
    desc: pick('Examination, X-ray diagnostics and relief of acute pain.', 'Огляд, рентген-діагностика та зняття гострого болю.', 'Осмотр, рентген-диагностика и снятие острой боли.', 'Examinare, diagnostic radiologic și ameliorarea durerii acute.')
  }, {
    step: '04',
    title: pick('Plan', 'План', 'План', 'Plan'),
    desc: pick('If further treatment is needed, we will create an action plan.', 'Якщо потрібне подальше лікування, ми складемо план дій.', 'Если нужно дальнейшее лечение, мы составим план действий.', 'Dacă este necesar un tratament suplimentar, vom crea un plan de acțiune.')
  }];
  const treatments = [{
    title: pick('Acute pain relief', 'Зняття гострого болю', 'Снятие острой боли', 'Ameliorarea durerii acute'),
    desc: pick('Medication and a temporary filling.', 'Медикаментозна обробка та тимчасова пломба.', 'Медикаментозная обработка и временная пломба.', 'Medicație și o plombă temporară.')
  }, {
    title: pick('Tooth extraction', 'Видалення зуба', 'Удаление зуба', 'Extracție dentară'),
    desc: pick('If the tooth cannot be saved or is a source of infection.', 'Якщо зуб неможливо врятувати або він є джерелом інфекції.', 'Если зуб невозможно спасти или он является источником инфекции.', 'Dacă dintele nu poate fi salvat sau este o sursă de infecție.')
  }, {
    title: pick('Root canal treatment', 'Лікування каналів', 'Лечение каналов', 'Tratament de canal'),
    desc: pick('Removal of the inflamed nerve (pulpectomy) to save the tooth.', 'Видалення запаленого нерва (пульпектомія) для порятунку зуба.', 'Удаление воспалённого нерва (пульпэктомия) для спасения зуба.', 'Îndepărtarea nervului inflamat (pulpectomie) pentru a salva dintele.')
  }, {
    title: pick('Crown re-fitting', 'Фіксація коронки', 'Фиксация коронки', 'Refixarea coroanei'),
    desc: pick('Re-cementing a crown or bridge that has come loose.', 'Цементування коронки або моста, що випали.', 'Цементирование выпавшей коронки или моста.', 'Recimentarea unei coroane sau punți care s-a desprins.')
  }, {
    title: pick('Abscess drainage', 'Розкриття абсцесу', 'Вскрытие абсцесса', 'Drenarea abcesului'),
    desc: pick('Draining pus to relieve swelling and pain.', 'Дренаж гною для зняття набряку та болю.', 'Дренаж гноя для снятия отёка и боли.', 'Drenarea puroiului pentru a ameliora umflarea și durerea.')
  }, {
    title: pick('Tooth splinting', 'Шинування зубів', 'Шинирование зубов', 'Imobilizarea dinților'),
    desc: pick('Stabilising loose teeth after trauma.', 'Фіксація рухомих зубів після травми.', 'Фиксация подвижных зубов после травмы.', 'Stabilizarea dinților mobili după o traumă.')
  }];
  const firstAid = [{
    title: pick('For toothache', 'При зубному болю', 'При зубной боли', 'Pentru durerea de dinți'),
    desc: pick('Take a painkiller (ibuprofen or paracetamol) and rinse your mouth with warm water. Do not place aspirin on the gums and do not apply heat to the affected area!', 'Прийміть знеболююче (ібупрофен або парацетамол), прополощіть рот теплою водою. Не прикладайте аспірин до ясен і не грійте хворе місце!', 'Примите обезболивающее (ибупрофен или парацетамол), прополощите рот тёплой водой. Не прикладывайте аспирин к дёснам и не грейте больное место!', 'Luați un analgezic (ibuprofen sau paracetamol) și clătiți gura cu apă caldă. Nu puneți aspirină pe gingii și nu aplicați căldură pe zona afectată!')
  }, {
    title: pick('If a tooth is knocked out', 'Якщо вибили зуб', 'Если выбили зуб', 'Dacă un dinte cade'),
    desc: pick('Hold the tooth by the crown (the white part) only. If dirty, rinse with milk or water. Try to place it back in the socket or store it in milk/saliva. Time is critical — you have 30-60 minutes!', 'Тримайте зуб тільки за коронку (білу частину). Якщо брудний — промийте молоком або водою. Спробуйте вставити назад у лунку або покладіть у молоко/слину. Час критичний — у вас є 30-60 хвилин!', 'Держите зуб только за коронку (белую часть). Если грязный — промойте молоком или водой. Попробуйте вставить обратно в лунку или положите в молоко/слюну. Время критично — у вас есть 30-60 минут!', 'Țineți dintele doar de coroană (partea albă). Dacă este murdar, clătiți-l cu lapte sau apă. Încercați să-l puneți înapoi în alveolă sau păstrați-l în lapte/salivă. Timpul este critic — aveți 30-60 de minute!')
  }, {
    title: pick('For swelling', 'При набряку', 'При отёке', 'Pentru umflături'),
    desc: pick('Apply a cold compress to the outside of the cheek. This will reduce swelling and pain. Do not use heat!', 'Прикладіть холодний компрес до щоки ззовні. Це зменшить набряк і біль. Не використовуйте тепло!', 'Приложите холодный компресс к щеке снаружи. Это уменьшит отёк и боль. Не используйте тепло!', 'Aplicați o compresă rece pe exteriorul obrazului. Acest lucru va reduce umflarea și durerea. Nu folosiți căldură!')
  }];
  return <Layout title={seoTitle} description={seoDescription} data-id="element-1622">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1623" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1624" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1625" />

      {/* 1. HERO SECTION (Urgent styling) */}
      <section className="relative bg-white overflow-hidden" data-id="element-1626">
        <div className="absolute inset-0 bg-red-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1627" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1628">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1629">
            <div className="space-y-6" data-id="element-1630">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium animate-pulse" data-id="element-1631">
                <AlertCircle className="w-4 h-4" data-id="element-1632" />
                {pick('Emergency Dental Care', 'Невідкладна допомога', 'Неотложная помощь', 'Urgență dentară')}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1633">
                {pick('Emergency Dentist in London — ', 'Невідкладна стоматологічна допомога у Лондоні — ', 'Неотложная стоматологическая помощь в Лондоне — ', 'Dentist de urgență în Londra — ')}
                <span className="text-red-600" data-id="element-1634">Dentico Clinic</span>
                <br data-id="element-1635" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1636">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1637">
                {pick('Severe toothache? Broken tooth? Swelling? We offer urgent dental appointments for patients with acute dental pain, dental trauma, broken teeth and swelling.', 'Гострий біль? Зламаний зуб? Набряк? Ми приймаємо пацієнтів з гострим болем, травмою зуба, зламаними зубами та набряком.', 'Острая боль? Сломанный зуб? Отёк? Мы принимаем пациентов с острой зубной болью, травмой зуба, сломанными зубами и отёком.', 'Durere dentară severă? Dinte fracturat? Umflătură? Oferim programări urgente pentru pacienți cu durere acută, traumă dentară, dinți fracturați și umflături.')}
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-1638">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1639">
                  <Clock className="w-4 h-4 text-[#2F4A3F]" data-id="element-1640" />
                  {pick('Urgent appointments', 'Терміновий прийом', 'Срочный приём', 'Programări urgente')}
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1641">
                  <MapPin className="w-4 h-4 text-[#2F4A3F]" data-id="element-1642" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1643">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-1644" />
                  {pick('Multilingual team', 'Українська мова', 'Русский язык', 'Echipă multilingvă')}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 pt-4" data-id="element-1645">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1646">
                  <Button variant="primary" size="lg" className="w-full gap-2" data-id="element-1647">
                    <AlertCircle className="w-5 h-5" data-id="element-1648" />
                    {pick('Book Emergency Appointment', 'Записатися на терміновий прийом', 'Записаться на срочный приём', 'Programează o urgență dentară')}
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1649">
                  <Button variant="whatsapp" size="lg" className="w-full gap-2" data-id="element-1650">
                    <MessageCircle className="w-5 h-5" data-id="element-1651" />
                    {pick('WhatsApp Us', 'Написати у WhatsApp', 'Написать в WhatsApp', 'Scrie-ne pe WhatsApp')}
                  </Button>
                </a>
                <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1652">
                  <Button variant="secondary" size="lg" className="w-full gap-2" data-id="element-1653">
                    <Phone className="w-5 h-5" data-id="element-1654" />
                    {pick('Call Clinic', 'Зателефонувати', 'Позвонить', 'Sună clinica')}
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1655">
              <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1656"></div>
              <img src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80" alt={pick('Emergency dentistry in London', 'Невідкладна стоматологія Лондон', 'Неотложная стоматология Лондон', 'Stomatologie de urgență în Londra')} className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full grayscale-[20%]" data-id="element-1657" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHEN TO SEEK HELP */}
      <section className="py-16 bg-white" data-id="element-1658">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1659">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1660">
            {pick('When do you need emergency care?', 'Коли потрібна невідкладна допомога?', 'Когда нужна неотложная помощь?', 'Când aveți nevoie de îngrijire de urgență?')}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1661">
            {whenCards.map((item, idx) => <Card key={idx} className="p-6 border-l-4 border-l-red-500" data-id="element-1662">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1663">
                  <AlertCircle className="w-5 h-5 text-red-500" data-id="element-1664" />
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1665">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3. FIRST AID */}
      <section className="py-16 bg-red-50" data-id="element-1666">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1667">
          <div className="text-center mb-10" data-id="element-1668">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1669">
              {pick('What to do before seeing the dentist?', 'Що робити до візиту до лікаря?', 'Что делать до визита к врачу?', 'Ce să faceți înainte de a merge la dentist?')}
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1670">
              {pick('First aid tips while you wait for your appointment.', 'Поради першої допомоги, поки ви чекаєте на прийом.', 'Советы по первой помощи, пока вы ждёте приёма.', 'Sfaturi de prim ajutor în timp ce așteptați programarea.')}
            </p>
          </div>
          <div className="space-y-4" data-id="element-1671">
            {firstAid.map((item, idx) => <div key={idx} className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1672">
                <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1673">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1674">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. OUR PROCESS */}
      <section className="py-16 bg-white" data-id="element-1675">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1676">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1677">
            {pick('How we handle emergencies', 'Як ми працюємо в екстрених випадках', 'Как мы работаем в экстренных случаях', 'Cum gestionăm urgențele')}
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1678">
            {processSteps.map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1679">
                <div className="text-6xl font-bold text-red-50 absolute -top-4 -right-4 select-none" data-id="element-1680">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1681">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1682">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1683">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 5. COMMON TREATMENTS */}
      <section className="py-16 bg-gray-50" data-id="element-1684">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1685">
          <div className="text-center mb-12" data-id="element-1686">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1687">
              {pick('Types of emergency care', 'Види невідкладної допомоги', 'Виды неотложной помощи', 'Tipuri de îngrijire de urgență')}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1688">
            {treatments.map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1689">
                <Zap className="w-8 h-8 text-red-500 mb-4" data-id="element-1690" />
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1691">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1692">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA (Urgent) */}
      <section className="py-12 bg-red-600" data-id="element-1693">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1694">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-1695">
            {pick('Tooth hurting right now?', 'Болить зуб прямо зараз?', 'Болит зуб прямо сейчас?', 'Vă doare un dinte chiar acum?')}
          </h2>
          <p className="text-red-100 mb-6" data-id="element-1696">
            {pick("Don't wait — message or call us for an urgent appointment.", 'Не чекайте — напишіть нам або зателефонуйте для термінового прийому.', 'Не ждите — напишите нам или позвоните для срочного приёма.', 'Nu așteptați — scrieți-ne sau sunați-ne pentru o programare urgentă.')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-1697">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1698">
              <Button variant="whatsappInverse" size="lg" className="gap-2" data-id="element-1699">
                <MessageCircle className="w-5 h-5" data-id="element-1700" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+447345197820" data-id="element-1701">
              <Button variant="outlineInverse" size="lg" className="gap-2" data-id="element-1702">
                <Phone className="w-5 h-5" data-id="element-1703" />
                +44 7345 197820
              </Button>
            </a>
            <Button variant="outlineInverse" size="lg" className="gap-2" onClick={() => setIsCallbackOpen(true)} data-id="element-1704">
              <Phone className="w-5 h-5" data-id="element-1705" />
              {pick('Request a Call', 'Замовити дзвінок', 'Заказать звонок', 'Solicitați un apel')}
            </Button>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1706">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1707">
          <div className="text-center mb-10" data-id="element-1708">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1709">
              {pick('Frequently Asked Questions', 'Часті запитання', 'Частые вопросы', 'Întrebări frecvente')}
            </h2>
          </div>
          <div className="space-y-2" data-id="element-1710">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1711" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1712">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1713">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1714">
            {pick('See also', 'Дивіться також', 'Смотрите также', 'Vedeți și')}
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1715">
            <Link to={`/${lang}/ukrainskyi-stomatolog-london`} data-id="element-1716">
              <Button variant="outline" size="sm" data-id="element-1717">
                {pick('Ukrainian Dentist', 'Український стоматолог', 'Украинский стоматолог', 'Dentist ucrainean')}
              </Button>
            </Link>
            <Link to={`/${lang}/root-canal-london`} data-id="element-1718">
              <Button variant="outline" size="sm" data-id="element-1719">
                {pick('Root Canal Treatment', 'Лікування каналів', 'Лечение каналов', 'Tratament de canal')}
              </Button>
            </Link>
            <Link to={`/${lang}/implanty-london`} data-id="element-1720">
              <Button variant="outline" size="sm" data-id="element-1721">
                {pick('Implants', 'Імплантація', 'Имплантация', 'Implanturi')}
              </Button>
            </Link>
            <Link to={`/${lang}/veneers-london`} data-id="element-1722">
              <Button variant="outline" size="sm" data-id="element-1723">
                {pick('Veneers', 'Вініри', 'Виниры', 'Fațete')}
              </Button>
            </Link>
            <Link to={`/${lang}/services/extraction`} data-id="element-1724">
              <Button variant="outline" size="sm" data-id="element-1725">
                {pick('Tooth Extraction', 'Видалення зубів', 'Удаление зубов', 'Extracție dentară')}
              </Button>
            </Link>
            <Link to={`/${lang}/prices`} data-id="element-1726">
              <Button variant="outline" size="sm" data-id="element-1727">
                {pick('Prices', 'Ціни', 'Цены', 'Prețuri')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA (Urgent) */}
      <section className="py-16 bg-red-900" data-id="element-1728">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1729">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1730">
            {pick('Need urgent help?', 'Потрібна термінова допомога?', 'Нужна срочная помощь?', 'Aveți nevoie de ajutor urgent?')}
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-1731">
            {pick("Don't wait for the pain to get worse. Contact us now.", "Не чекайте, поки біль посилиться. Зв'яжіться з нами зараз.", 'Не ждите, пока боль усилится. Свяжитесь с нами сейчас.', 'Nu așteptați ca durerea să se agraveze. Contactați-ne acum.')}
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-4" data-id="element-1732">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1733">
              <Button variant="primaryInverse" size="lg" className="w-full gap-2" data-id="element-1734">
                <AlertCircle className="w-5 h-5" data-id="element-1735" />
                {pick('Book Emergency Appointment', 'Записатися на терміновий прийом', 'Записаться на срочный приём', 'Programează o urgență dentară')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1736">
              <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-1737">
                <MessageCircle className="w-5 h-5" data-id="element-1738" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-1739">
              <Button variant="outlineInverse" size="lg" className="w-full gap-2" onClick={() => setIsCallbackOpen(true)} data-id="element-1740">
                <Phone className="w-5 h-5" data-id="element-1741" />
                {pick('Request a Call', 'Замовити дзвінок', 'Заказать звонок', 'Solicitați un apel')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}